import { useMemo } from "react";
import { Entity } from "@common/types/entity";
import { currencyFormatter } from "@common/helpers/currency";
import {
    getTotalBalance,
    useFinancialAccounts,
} from "@hooks/useFinancialAccounts";
import { Segment } from "@helpers/segment";
import { SegmentBar } from "@design-system/SegmentBar/SegmentBar";
import styles from "./AccountsSegmentBar.module.scss";

interface Props {
    entities: Entity[];
    selectedEntityId?: number | null;
    onEntityClick?: (entityId: number) => void;
}

const AccountStatTooltip: React.FC<{
    label: string;
    valueOverride: string;
    subtitle: React.ReactNode;
}> = ({ label, valueOverride, subtitle }) => (
    <div className={styles.accountTooltip}>
        <span className={styles.headline}>{label}</span>
        <span className={styles.value}>{valueOverride}</span>
        <span className={styles.subValue}>{subtitle}</span>
    </div>
);

function useAccountsSegments(
    entities: Entity[],
    selectedEntityId?: number | null,
): Segment[] {
    const allAccounts = useFinancialAccounts({
        entityIds: entities.map((e) => e.id),
    });

    return useMemo(() => {
        const isEntityFilterApplied =
            selectedEntityId !== null && selectedEntityId !== undefined;
        const totalBalance = getTotalBalance(
            allAccounts.filter(
                (account) =>
                    !isEntityFilterApplied ||
                    account.entityId === selectedEntityId,
            ),
        );

        return entities.map((entity) => {
            const color = entity.user?.iconColor ?? entity.iconColor;
            const isSelectedEntity =
                selectedEntityId === null ||
                selectedEntityId === undefined ||
                entity.id === selectedEntityId;
            const entityAccounts = allAccounts.filter(
                (a) => a.entityId === entity.id,
            );
            const balance = getTotalBalance(entityAccounts);
            const visibleBalance = isSelectedEntity ? balance : 0;
            const subtitle =
                totalBalance > 0
                    ? `${((visibleBalance / totalBalance) * 100).toFixed(1)}%`
                    : "0%";
            const valueOverride = currencyFormatter.format(visibleBalance);
            const positiveEntityAccounts = entityAccounts.filter(
                (account) => !account.disabled && (account.balance ?? 0) > 0,
            );

            return {
                id: String(entity.id),
                label: entity.name ?? "",
                value: visibleBalance,
                valueOverride,
                subtitle,
                colors: {
                    start: color,
                    subtitle: "#0f182680",
                },
                subsegments: positiveEntityAccounts.map((account) => ({
                    id: `account-${account.id}`,
                    label: account.name ?? "",
                    value: isSelectedEntity ? (account.balance ?? 0) : 0,
                    valueOverride: currencyFormatter.format(
                        isSelectedEntity ? (account.balance ?? 0) : 0,
                    ),
                    subtitle:
                        balance > 0
                            ? `${(((account.balance ?? 0) / balance) * 100).toFixed(1)}%`
                            : "0%",
                    tooltip: (
                        <AccountStatTooltip
                            label={account.name ?? ""}
                            valueOverride={currencyFormatter.format(
                                account.balance ?? 0,
                            )}
                            subtitle={
                                balance > 0
                                    ? `${(((account.balance ?? 0) / balance) * 100).toFixed(1)}%`
                                    : "0%"
                            }
                        />
                    ),
                })),
                tooltip: (
                    <AccountStatTooltip
                        label={entity.name ?? ""}
                        valueOverride={valueOverride}
                        subtitle={subtitle}
                    />
                ),
            };
        });
    }, [entities, allAccounts, selectedEntityId]);
}

export const AccountsSegmentBar: React.FC<Props> = ({
    entities,
    selectedEntityId,
    onEntityClick,
}) => {
    const segments = useAccountsSegments(entities, selectedEntityId);

    if (!segments.length) {
        return null;
    }

    return (
        <div className={styles.container}>
            <SegmentBar
                segments={segments}
                formatValueAsMoney
                hasMinimumWidth
                showSubsegmentsOnHover
                onSegmentClick={(segmentId) => {
                    const entityId = Number(segmentId);

                    if (Number.isNaN(entityId)) {
                        return;
                    }

                    onEntityClick?.(entityId);
                }}
                className={styles.bar}
            />
        </div>
    );
};
